import {
  ChangeEvent,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useState,
} from "react";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Skeleton from "@mui/material/Skeleton";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import {
  getETaxDocuments,
  createETaxBatch,
  checkETaxEligibility,
} from "../services";
import {
  ETaxDocument,
  ETaxDocumentFilter,
  ETaxBatchItemPayload,
  ETaxBlockedDocument,
  ETaxEligibilityCheckResponse,
  ETaxCreateBatchResult,
} from "../types/e-tax.type";
import { formatDate } from "../utils/date";
import { formatNumber, numberWithCommas } from "../utils";
import { useDebounce } from "../utils/hooks/useDebounce";
import { COMPACT_QUERY } from "../utils/hooks/useCompact";
import ETaxPagination from "../components/ETax/Pagination";
import DocumentFilter from "../components/ETax/DocumentFilter";
import {
  DocTypeChip,
  headCellSx,
} from "../components/ETax/documentCells";
import { EtaxDocumentStatusCell } from "../components/ETax/AutoSend/autoSendCells";
import { useEtaxTransientRetryMax } from "../utils/hooks/useEtaxTransientRetryMax";
import ConfirmSendModal, {
  SendSummary,
} from "../components/ETax/ConfirmSendModal";
import SendPageHeader from "../components/ETax/SendPageHeader";
import SendSummaryFooter from "../components/ETax/SendSummaryFooter";
import { useSendDateRange } from "../components/ETax/useSendDateRange";
import { reportSendError } from "../components/ETax/reportSendError";
import { ETAX_SEND_PATH } from "../components/ETax/sendPaths";
import SuccessModal from "../components/ETax/SuccessModal";
import SendProblemModal, {
  SendProblem,
} from "../components/ETax/SendProblemModal";
import {
  SEND_CHUNK_SIZE,
  SendResultSummary,
  buildSendSummary,
  chunk,
  clampPage,
  extractBatchRefusals,
  getUnsendableReason,
  isUnsendable,
  mergeBatchResults,
} from "../components/ETax/sendBatch";

const compact = "@media " + COMPACT_QUERY;
const LIMIT = 10;
const COLUMN_COUNT = 12;

const toBatchItem = (doc: ETaxDocument): ETaxBatchItemPayload => ({
  sales_report_sk: doc.sales_report_sk,
  sales_report_uuid: doc.sales_report_uuid,
  transaction_uuid: doc.transaction_uuid,
  transaction_no: doc.transaction_no,
  transaction_type: doc.transaction_type,
});

const renderSkeletonRows = () =>
  Array.from({ length: LIMIT }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: COLUMN_COUNT }).map((__, colIndex) => (
        <TableCell key={colIndex}>
          <Skeleton variant="text" />
        </TableCell>
      ))}
    </TableRow>
  ));

export default function ETaxSendPage() {
  const retryMax = useEtaxTransientRetryMax();
  const {
    startDate,
    endDate,
    changeDateRange: handleChangeDateRange,
    dateSearch,
    isRangeInvalid: rangeInvalid,
  } = useSendDateRange();

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(LIMIT);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [documents, setDocuments] = useState<ETaxDocument[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [selected, setSelected] = useState<Record<string, ETaxDocument>>({});
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] =
    useState<ETaxEligibilityCheckResponse | null>(null);

  const [documentType, setDocumentType] = useState<string>("all");
  const [branchCode, setBranchCode] = useState<string>("all");
  const [routeCode, setRouteCode] = useState<string>("all");
  const [customerName, setCustomerName] = useState<string>("");
  const [taxId, setTaxId] = useState<string>("");

  const customerNameSearch = useDebounce(customerName, 500);
  const taxIdSearch = useDebounce(taxId, 500);

  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [sendAllMode, setSendAllMode] = useState<boolean>(false);
  const [allDocs, setAllDocs] = useState<ETaxDocument[]>([]);
  const [preparing, setPreparing] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [successOpen, setSuccessOpen] = useState<boolean>(false);
  const [sendResult, setSendResult] = useState<SendResultSummary | null>(null);
  const [sendProblem, setSendProblem] = useState<SendProblem | null>(null);

  const buildFilter = useCallback(
    (): ETaxDocumentFilter => ({
      dateFrom: startDate,
      dateTo: endDate,
      sendState: "PENDING",
      documentType,
      branchCode,
      routeCode,
      customerName: customerNameSearch,
      taxId: taxIdSearch,
    }),
    [
      startDate,
      endDate,
      documentType,
      branchCode,
      routeCode,
      customerNameSearch,
      taxIdSearch,
    ],
  );

  // A selection belongs to the filter it was made under. Bills picked on another date range,
  // branch or route are no longer on screen, and sending them would surprise the operator.
  useEffect(() => {
    setSelected({});
  }, [buildFilter]);

  // Only the newest request may write state. Changing the date range fires a request
  // per picker edit; without this guard a slower earlier response can land last and
  // repaint the table with rows from the previous filter.
  const requestSeq = useRef<number>(0);

  const fetchDocuments = useCallback(async () => {
    // A start-after-end range matches nothing; DocumentFilter already flags it on the
    // pickers, so skip the request rather than render a misleading empty table.
    if (rangeInvalid) return;

    const seq = requestSeq.current + 1;
    requestSeq.current = seq;

    setLoading(true);
    try {
      const {
        data: {
          data: { rows, count },
        },
      } = await getETaxDocuments(page, limit, buildFilter());
      if (requestSeq.current !== seq) return;
      // Sending (or anything else) can shrink the list under the current page. Step back to
      // the last page that still has rows; the page change refetches on its own.
      const lastUsefulPage = clampPage(page, count, limit);
      if (rows.length === 0 && lastUsefulPage !== page) {
        setPage(lastUsefulPage);
        return;
      }
      setDocuments(rows);
      setTotalCount(count);
    } catch {
      // The axios interceptor surfaces the error toast; reset to an empty state
      // so the table is never stuck on the loading skeleton.
      if (requestSeq.current !== seq) return;
      setDocuments([]);
      setTotalCount(0);
    } finally {
      if (requestSeq.current === seq) setLoading(false);
    }
  }, [page, limit, buildFilter, rangeInvalid]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const selectedDocs = useMemo(() => Object.values(selected), [selected]);
  const selectedSummary = useMemo(
    () => buildSendSummary(selectedDocs),
    [selectedDocs],
  );

  const toggleRow = (doc: ETaxDocument) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[doc.transaction_uuid]) {
        delete next[doc.transaction_uuid];
      } else {
        next[doc.transaction_uuid] = doc;
      }
      return next;
    });
  };

  // Refused by the gate, past its tax month, or already rejected by KTB / the Revenue
  // Department: createETaxBatch would reject the whole request if any were included, so
  // they cannot be picked (see getUnsendableReason).
  const selectableDocuments = useMemo(
    () => documents.filter((doc) => !isUnsendable(doc)),
    [documents],
  );

  const handleSelectAllOnPage = (e: ChangeEvent<HTMLInputElement>) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (e.target.checked) {
        selectableDocuments.forEach((doc) => {
          next[doc.transaction_uuid] = doc;
        });
      } else {
        documents.forEach((doc) => delete next[doc.transaction_uuid]);
      }
      return next;
    });
  };

  const allOnPageSelected =
    selectableDocuments.length > 0 &&
    selectableDocuments.every((doc) => selected[doc.transaction_uuid]);
  const someOnPageSelected = selectableDocuments.some(
    (doc) => selected[doc.transaction_uuid],
  );

  /**
   * Run the gate over every bill matching the current filter without submitting anything.
   *
   * This is the only thing that puts a verdict on a bill: nothing checks them on a schedule,
   * so until somebody presses this (or presses send), an unsendable bill is indistinguishable
   * from a sendable one. Pressing it is free and repeatable.
   */
  const handleCheckAll = async () => {
    if (totalCount === 0) return;
    setChecking(true);
    try {
      const {
        data: {
          data: { rows },
        },
      } = await getETaxDocuments(1, totalCount, buildFilter());

      const items = rows.map((row: ETaxDocument) => ({
        transaction_uuid: row.transaction_uuid,
      }));
      const {
        data: { data: result },
      } = await checkETaxEligibility(items);

      setCheckResult(result);
      // Anything the gate just refused must not stay selected, or the next send would be
      // rejected wholesale for a bill the operator can already see is unsendable.
      const blockedUuids = new Set(
        result.blocked.map((row: ETaxBlockedDocument) => row.transaction_uuid),
      );
      setSelected((prev) => {
        const next = { ...prev };
        blockedUuids.forEach((uuid) => delete next[uuid as string]);
        return next;
      });
      fetchDocuments();
    } catch {
      // Surfaced by the axios interceptor.
    } finally {
      setChecking(false);
    }
  };

  const openConfirmSelected = () => {
    setSendAllMode(false);
    setConfirmOpen(true);
  };

  // Send-all: fetch every PENDING bill matching the current filter so the confirmation totals
  // and the submitted set are derived from the exact same rows.
  const openConfirmAll = async () => {
    if (totalCount === 0) return;
    setSendAllMode(true);
    setPreparing(true);
    setConfirmOpen(true);
    try {
      const {
        data: {
          data: { rows },
        },
      } = await getETaxDocuments(1, totalCount, buildFilter());
      // "ส่งทั้งหมด" means every bill that CAN go. Leaving the refused ones in would make
      // the whole request bounce and send nothing at all.
      setAllDocs(rows.filter((row: ETaxDocument) => !isUnsendable(row)));
    } catch {
      // Surfaced by the axios interceptor; close the confirm dialog so the user
      // can adjust filters and retry instead of confirming an empty send.
      setAllDocs([]);
      setConfirmOpen(false);
    } finally {
      setPreparing(false);
    }
  };

  const confirmSummary: SendSummary = sendAllMode
    ? buildSendSummary(allDocs)
    : selectedSummary;

  /**
   * One chunk was rejected. Chunks before it are already written, so they must leave the
   * selection and be reported; the rejected chunk and everything after it were not sent.
   *
   * A gate refusal (400 + per-bill list) rejects its whole chunk: the refused bills leave the
   * selection so the operator can simply press send again. Any other failure on the very
   * first chunk keeps the confirm dialog open for a retry, as before.
   */
  const handleSendFailure = (
    error: unknown,
    acceptedUuids: string[],
    notSentCount: number,
  ) => {
    const refusals = extractBatchRefusals(error);
    if (!refusals) reportSendError(error);
    if (!refusals && acceptedUuids.length === 0) return;

    const leaving = new Set<string>([
      ...acceptedUuids,
      ...(refusals ?? []).map((row) => row.transaction_uuid),
    ]);
    setSelected((prev) => {
      const next = { ...prev };
      leaving.forEach((uuid) => delete next[uuid]);
      return next;
    });
    setConfirmOpen(false);
    setAllDocs([]);
    setSendProblem({
      refusals: refusals ?? [],
      alreadySentCount: acceptedUuids.length,
      notSentCount,
    });
    fetchDocuments();
  };

  const handleConfirmSend = async () => {
    const docsToSend = sendAllMode ? allDocs : selectedDocs;
    if (docsToSend.length === 0 || sending) return;

    setSending(true);
    try {
      const stamp = dayjs().format("YYYY-MM-DD HH:mm");
      const batches = chunk(docsToSend, SEND_CHUNK_SIZE);
      const results: Array<ETaxCreateBatchResult | null> = [];
      const acceptedUuids: string[] = [];

      for (let i = 0; i < batches.length; i += 1) {
        const batchName =
          batches.length > 1
            ? `E-Tax ${stamp} (${i + 1}/${batches.length})`
            : `E-Tax ${stamp}`;
        let result: ETaxCreateBatchResult | null;
        try {
          const response = await createETaxBatch({
            batch_name: batchName,
            items: batches[i].map(toBatchItem),
          });
          // An older backend answers `data: null`; the result dialog then only says "สำเร็จ".
          result = response.data?.data ?? null;
        } catch (error) {
          handleSendFailure(
            error,
            acceptedUuids,
            docsToSend.length - acceptedUuids.length,
          );
          return;
        }
        results.push(result);
        batches[i].forEach((doc) => acceptedUuids.push(doc.transaction_uuid));
      }

      setConfirmOpen(false);
      setSendResult(mergeBatchResults(results));
      setSuccessOpen(true);
      setSelected({});
      setAllDocs([]);
      fetchDocuments();
    } finally {
      setSending(false);
    }
  };

  return (
    // Bottom padding clears the fixed summary footer so the table's last rows and
    // pagination stay reachable above it (the footer is position: fixed).
    <Box sx={{ px: 0, pt: "8px", pb: "88px", [compact]: { pb: "70px" } }}>
      <SendPageHeader current={ETAX_SEND_PATH} dateSearch={dateSearch} />

      <Paper>
        <DocumentFilter
          documentType={documentType}
          setDocumentType={setDocumentType}
          etaxStatus="all"
          setEtaxStatus={() => undefined}
          branchCode={branchCode}
          setBranchCode={setBranchCode}
          routeCode={routeCode}
          setRouteCode={setRouteCode}
          customerName={customerName}
          setCustomerName={setCustomerName}
          taxId={taxId}
          setTaxId={setTaxId}
          dateFrom={startDate}
          dateTo={endDate}
          onChangeDateRange={handleChangeDateRange}
          onChange={() => setPage(1)}
          showStatusFilter={false}
        />

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          mb={2}
          sx={{ [compact]: { flexWrap: "wrap" } }}
        >
          <Typography variant="body2" color="text.secondary">
            ค้างส่งทั้งหมด{" "}
            <Box component="span" fontWeight={700} color="text.primary">
              {numberWithCommas(totalCount)}
            </Box>{" "}
            รายการ · เลือกแล้ว{" "}
            <Box component="span" fontWeight={700} color="text.primary">
              {numberWithCommas(selectedSummary.documentCount)}
            </Box>{" "}
            รายการ
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              onClick={handleCheckAll}
              disabled={totalCount === 0 || checking}
            >
              {checking ? "กำลังตรวจสอบ..." : "ตรวจสอบ"}
            </Button>
            <Button
              variant="outlined"
              onClick={openConfirmAll}
              disabled={totalCount === 0}
            >
              ส่งทั้งหมด
            </Button>
          </Box>
        </Box>

        {checkResult && (
          <Alert
            severity={checkResult.blocked.length > 0 ? "warning" : "success"}
            sx={{ mb: 2 }}
            onClose={() => setCheckResult(null)}
          >
            ตรวจสอบ {numberWithCommas(checkResult.checked_count)} รายการ · ส่งได้{" "}
            {numberWithCommas(checkResult.eligible_count)} รายการ
            {checkResult.blocked.length > 0 && (
              <>
                {" "}· <b>ส่งไม่ได้ {numberWithCommas(checkResult.blocked.length)} รายการ</b>{" "}
                — บิลที่ส่งไม่ได้ถูกติดสถานะไว้แล้ว และเอาออกจากรายการที่เลือกให้อัตโนมัติ
              </>
            )}
          </Alert>
        )}

        <TableContainer sx={{ borderRadius: "12px" }}>
          <Table>
            <TableHead
              sx={{ bgcolor: (theme) => theme.palette.background.default }}
            >
              <TableRow>
                <TableCell padding="checkbox" sx={headCellSx}>
                  <Checkbox
                    checked={allOnPageSelected}
                    indeterminate={someOnPageSelected && !allOnPageSelected}
                    onChange={handleSelectAllOnPage}
                  />
                </TableCell>
                <TableCell sx={headCellSx}>#</TableCell>
                <TableCell sx={headCellSx}>สร้าง</TableCell>
                <TableCell sx={headCellSx}>เลขที่บิล</TableCell>
                <TableCell sx={headCellSx}>Tax ID</TableCell>
                <TableCell sx={headCellSx}>ชื่อลูกค้า</TableCell>
                <TableCell sx={headCellSx}>ประเภท</TableCell>
                <TableCell sx={headCellSx}>สนญ.</TableCell>
                <TableCell align="right" sx={headCellSx}>
                  มูลค่าสินค้า
                </TableCell>
                <TableCell align="right" sx={headCellSx}>
                  ภาษีมูลค่าเพิ่ม (VAT)
                </TableCell>
                <TableCell align="right">รวมทั้งสิ้น</TableCell>
                <TableCell sx={headCellSx}>สถานะ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && renderSkeletonRows()}
              {!loading && documents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary" fontWeight={500}>
                      ไม่พบเอกสารค้างส่ง
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                documents.map((doc, index) => {
                  const checked = Boolean(selected[doc.transaction_uuid]);
                  const unsendableReason = getUnsendableReason(doc);
                  return (
                    <TableRow
                      key={doc.transaction_uuid}
                      hover
                      selected={checked}
                      sx={{
                        "& td, & th": { padding: "8px" },
                        bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                      }}
                    >
                      <TableCell padding="checkbox">
                        {unsendableReason ? (
                          <Tooltip arrow title={unsendableReason}>
                            {/* A disabled input fires no pointer events; the span takes the hover. */}
                            <span>
                              <Checkbox checked={false} disabled />
                            </span>
                          </Tooltip>
                        ) : (
                          <Checkbox
                            checked={checked}
                            onChange={() => toggleRow(doc)}
                          />
                        )}
                      </TableCell>
                      <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                      <TableCell>{formatDate(doc.transaction_date)}</TableCell>
                      <TableCell>{doc.transaction_no}</TableCell>
                      <TableCell>{doc.tax_id ?? "-"}</TableCell>
                      <TableCell>{doc.customer_name}</TableCell>
                      <TableCell>
                        <DocTypeChip type={doc.document_type} />
                      </TableCell>
                      <TableCell>{doc.branch_code ?? "-"}</TableCell>
                      <TableCell align="right">
                        {formatNumber(doc.amount_before_vat)}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(doc.vat_amount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(doc.net_total)}
                      </TableCell>
                      <TableCell>
                        <EtaxDocumentStatusCell doc={doc} retryMax={retryMax} />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <ETaxPagination
          totalCount={totalCount}
          page={page}
          setPage={setPage}
          limit={limit}
          setLimit={setLimit}
        />
      </Paper>

      <SendSummaryFooter
        summary={selectedSummary}
        countLabel="จำนวนบิล"
        onConfirm={openConfirmSelected}
      />

      <ConfirmSendModal
        open={confirmOpen}
        summary={confirmSummary}
        loading={sending || preparing}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSend}
      />
      <SuccessModal
        open={successOpen}
        result={sendResult}
        onClose={() => setSuccessOpen(false)}
      />
      <SendProblemModal
        problem={sendProblem}
        onClose={() => setSendProblem(null)}
      />
    </Box>
  );
}
